import type {
  Defect,
  DefectFailedStep,
  DefectStatus,
  E2eReleaseCreate,
  FixCheck,
  FixCheckConfirm,
  NotedStepDefectList,
  PlaylistLastResult,
  StepCheckList,
  StepDefectCreate,
  TestResult,
} from '@testflow/shared';
import { ApiError, request } from './api.js';

// Task 4 (A4)/Task 8 (A1): auf wen und worauf gewartet wird. Ein Name, eine Bedeutung — die
// Playlist-Karte und die Kettenleiste lesen dieselbe Form aus derselben Server-Auswertung
// (`predecessorGate`), deshalb steht sie hier einmal und nicht zweimal inline.
export interface ChainBlockedBy {
  title_de: string;
  title_en: string;
  sequence: number;
  tester_name: string | null;
}

export interface PlaylistEntry {
  test_case_id: string;
  human_id: string;
  title_de: string;
  title_en: string;
  tcode: string | null;
  e2e_id: string | null;
  // Task 10 (#113, A4): Der NAME der Kette, aus der dieser Eintrag stammt — die Karte zeigte bis
  // hierher nur ein namenloses Abzeichen. Beide Sprachen, die Oberflaeche waehlt nach
  // `i18n.resolvedLanguage`. An `e2e_id` gekoppelt: ohne Kette null.
  e2e_name_de: string | null;
  e2e_name_en: string | null;
  // #31 (Finding 25): 'error' = gemeldeter Fehler, noch kein Nachtest-Auftrag.
  // Task 4 (#113, A4): 'waiting' ERSETZT das frueher hier stehende 'up_next' — der direkte
  // Vorgaenger in dieser Kette ist noch nicht bestanden. Bewusst NICHT 'blocked': der Name
  // gehoert dem Lauf-Ergebnis "Blockiert" (playlist-groups.ts, gelb). Die Darstellung (grau,
  // ausgegraut, eigener Zaehler) kommt in Task 10.
  state: 'waiting' | 'retest' | 'error' | 'default';
  // Task 4 (A4): auf wen und worauf gewartet wird — "wartet auf Schulz — Schritt 2: Lieferung".
  //
  // DER VERTRAG (Whole-Branch-Review, Minor 1 + Important 2 — hier stand noch die zweimal
  // ueberholte Fassung „NUR gesetzt, wenn state === 'waiting'"): `blocked_by` ist vom `state`
  // ENTKOPPELT und traegt AUSSCHLIESSLICH das Vorgaenger-Urteil des Servers
  // (services/plan-status.ts, `predecessorGate`) — dasselbe, das den Lauf mit 409
  // `predecessor_not_done` abweist. Es steht also auch an einem Glied mit eigenem `error`- oder
  // `retest`-Lauf UND an einem bereits BESTANDENEN Glied, dessen Vorgaenger nachtraeglich wieder
  // rot geworden ist.
  //
  // Fuer die Frage „darf ein Lauf abgeschickt werden?" ist genau dieses Feld zu lesen
  // (execution-panel.tsx). Fuer die Frage „ist diese Karte zu sperren/auszugrauen/als wartend zu
  // zaehlen?" gibt es `isLocked` (lib/playlist-groups.ts) — es nimmt die eine Anzeige-Ruecksicht:
  // erledigt bleibt erledigt. Wer beides vermischt, baut den Befund nach, der dieses Feature
  // dreimal beschaeftigt hat.
  blocked_by: ChainBlockedBy | null;
  // #92: Kontext des retest-bereiten Defects (primär ODER #105-affected) — NUR gesetzt, wenn
  // state === 'retest'. UI kommt in Task 6 (Retest-Sektion + Defect-Chip). `title` ist einsprachig
  // (Freitext, wie defects.title im ganzen System) — kein title_de/title_en-Paar wie bei Testfällen.
  retest_defect: { defect_id: string; human_id: string; title: string } | null;
  // #50 (PLAN-5): gesetzt, wenn das Item einer vom aktuellen Tester vertretenen Person gehört.
  // Ownership bleibt beim Vertretenen; die Karte weist die Herkunft aus. null = eigenes Item.
  substituted_for: { user_id: string; name: string } | null;
  // #93: das rohe letzte Lauf-Ergebnis. `state` bleibt die abgeleitete AKTIONS-Sicht; die
  // Client-Gruppierung (Task 6–8) braucht zusätzlich das Ergebnis selbst, um „Erledigt" von
  // „Offen" zu unterscheiden.
  last_result: PlaylistLastResult | null;
  // #190: Wurde der blockierende Defect des letzten Fehlerlaufs abgelehnt, liefert die API
  // `last_result: null` (der Eintrag gilt wieder als „Offen") UND hier den Grund dafür, damit die
  // Karte erklären kann, warum der Testfall zurück ist. `reason` = jüngster Kommentar am Defect
  // (null, wenn keiner geschrieben wurde). Optional (Muster `Defect.stops_chain`): fehlt das Feld,
  // zählt das wie null — so bleiben die Playlist-Fixtures der Web-Tests gültig.
  rejected_defect?: {
    defect_id: string;
    human_id: string;
    reason: string | null;
  } | null;
}

export interface RunResult {
  run_id: string;
  test_plan_id: string;
  test_case_id: string;
  human_id: string;
  result: TestResult;
  result_description: string | null;
  actual_tester_id: string | null;
  failed_step_id: string | null;
  e2e_id: string | null;
  used_test_data_ids: string[];
  screenshots: string[];
  executed_at: string;
}

export interface RunSubmitBody {
  test_case_id: string;
  result: TestResult;
  result_description?: string | null;
  failed_step_id?: string | null;
  e2e_id?: string | null;
  used_test_data_ids?: string[];
  screenshots?: string[];
  // #90: optionale Severity-Wahl des Testers im Fehler-Dialog (nur beim Fehler-Submit gesetzt).
  severity_id?: string | null;
  // #126/#186: Wahl im Fehler-Dialog — true („Testfall nicht bestanden") nur mit result 'error',
  // false („Testfall bestanden", Fehler gemeldet) nur mit 'successful' (der Server prüft das: 422
  // stops_chain_mismatch). Weggelassen/null = Dialog nicht benutzt (Passt/Blockiert).
  stops_chain?: boolean | null;
  // #208 Nachtrag: notierte Fehler dieser Durchführung als Grund des Fehler-Laufs (nur mit
  // result 'error'); `create_defect` false = kein zusätzlicher Auto-Defect. Weggelassen = wie bisher.
  linked_defect_ids?: string[];
  create_defect?: boolean;
}

const base = (p: string) => `/v1/projects/${p}`;

// =================================================================================================
// Task 8 (#113, A1/A2/A5): die Kettensicht fuer die Kettenleiste.
//
// DREI Aufrufe, bewusst getrennt: `chain` traegt je Glied nur die ANZAHL der Anhaenge,
// `attachments` holt die Inhalte EINES Laufs — erst beim Aufklappen. Das ist kein
// Sparsamkeits-Geschmack, sondern Akzeptanzkriterium: Anhaenge liegen als data-URL mit bis zu
// 2 MB je Stueck und ohne Mengenlimit im jsonb (anforderung.md, Ausgangslage). Eine Kette mit
// vier erledigten Gliedern kann so 32 MB in eine Antwort ziehen, von denen der Tester im
// Regelfall keine einzige sieht.
// =================================================================================================
export interface ChainLastRun {
  run_id: string;
  result: TestResult;
  executed_at: string;
  result_description: string | null;
  // Lauf ueberlebt den Nutzer (`actual_tester_id` ist set null) — der Name kann fehlen.
  tester_name: string | null;
  // NUR die Zahl. Die Bilder holt `attachments` beim Aufklappen (s. o.).
  attachment_count: number;
  // #126: der Defect, der aus diesem Lauf entstand (error oder „bestanden mit Fehler"); null =
  // keiner. Nur Zuordnung für den Hinweis am Glied, kein Lauf-Ergebnis.
  // #190: `status` optional wie `Defect.stops_chain` — bestehende Fixtures bleiben gültig, die API
  // liefert ihn immer. Ein abgelehnter Defect an einem Fehler-Lauf zeigt das Glied neutral.
  defect: { defect_id: string; human_id: string; status?: DefectStatus } | null;
  // #190-Fix: abgelehnter Fehlerlauf laut Server (loadRejectedErrorRuns) — nur dieses Feld
  // entscheidet über die neutrale Anzeige, nicht `defect.status`. Optional wie `status`: bestehende
  // Fixtures bleiben gültig, die API liefert es immer.
  rejected?: boolean;
}

// A5: der Notausgang, wie ihn die Kettenleiste anzeigt („freigegeben von Meier: Beleg 4711
// manuell angelegt"). `released_by_name` ist nullable, weil `released_by` set null ist: die
// Begruendung ueberlebt das Loeschen des Nutzers, der Name nicht.
export interface ChainRelease {
  reason: string;
  released_by_name: string | null;
  released_at: string;
}

export interface ChainLink {
  test_case_id: string;
  human_id: string;
  title_de: string;
  title_en: string;
  sequence: number;
  assigned_tester_name: string | null;
  state: PlaylistEntry['state'];
  // Derselbe Vertrag wie an `PlaylistEntry.blocked_by` oben (dieselbe Server-Auswertung): das reine
  // Vorgaenger-Urteil, unabhaengig von `state` und unabhaengig davon, ob dieses Glied schon
  // bestanden ist. Die Kettenleiste blendet die Warteaussage an einem bestandenen Glied selbst aus
  // (chain-bar.tsx, `waitingFor`) — das Feld tut es nicht.
  blocked_by: ChainBlockedBy | null;
  // A2: „Je Testfall zaehlt nur der aktuelle (letzte) Lauf, keine Lauf-Historie."
  last_run: ChainLastRun | null;
  release: ChainRelease | null;
}

export interface ChainView {
  e2e_id: string;
  human_id: string;
  name_de: string;
  name_en: string;
  // A1: der Stream in der Kopfzeile. Kette ohne Stream-Knoten in diesem Projekt -> null.
  stream: { node_id: string; name: string } | null;
  // Serverseitig bereits nach Sequenz sortiert; die Auswertung „direkter Vorgaenger" arbeitet auf
  // dieser Liste, nicht auf `sequence ± 1` (Luecken durch abgewaehlte Glieder, s. architektur.md).
  links: ChainLink[];
}

export interface RunAttachments {
  run_id: string;
  screenshots: string[];
}

export interface E2eReleaseCreated {
  e2e_step_release_id: string;
  test_plan_id: string;
  e2e_id: string;
  test_case_id: string;
  reason: string;
  released_by: string | null;
  released_at: string;
  // Kommt aus der Sitzung des Freigebenden — ohne ihn muesste die Oberflaeche die ganze
  // Kettensicht neu laden, nur um „freigegeben von Meier" zu schreiben.
  released_by_name: string | null;
}

// Obergrenze der Ids je Kettenabruf — spiegelt CHAIN_BATCH_MAX in apps/api/src/routes/runs.ts
// (mehr liefert dort 400 invalid_e2e_ids). Wer eines ändert, ändert das andere.
const CHAIN_BATCH_MAX = 100;

// #208: Schritt-Haken eines Testfalls im Plan (Tester mit run.execute; GET liefert die eigenen Haken).
const stepChecksBase = (p: string, planId: string, testCaseId: string) =>
  `${base(p)}/plans/${planId}/testcases/${testCaseId}/step-checks`;

export const stepChecksApi = {
  list: (p: string, planId: string, testCaseId: string) =>
    request<StepCheckList>(stepChecksBase(p, planId, testCaseId)),
  check: (p: string, planId: string, testCaseId: string, stepId: string) =>
    request<void>(`${stepChecksBase(p, planId, testCaseId)}/${stepId}`, {
      method: 'PUT',
    }),
  uncheck: (p: string, planId: string, testCaseId: string, stepId: string) =>
    request<void>(`${stepChecksBase(p, planId, testCaseId)}/${stepId}`, {
      method: 'DELETE',
    }),
};

export const runsApi = {
  // #208: nicht blockierender Defect zu einem Schritt, ohne Ergebnis zu buchen.
  createStepDefect: (p: string, planId: string, testCaseId: string, body: StepDefectCreate) =>
    request<Defect>(`${base(p)}/plans/${planId}/testcases/${testCaseId}/step-defects`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #208 Nachtrag: eigene, noch offene notierte Fehler dieser Durchführung (Markierung am Schritt,
  // Auswahl in „Fehler melden“).
  listNotedStepDefects: (p: string, planId: string, testCaseId: string) =>
    request<NotedStepDefectList>(`${base(p)}/plans/${planId}/testcases/${testCaseId}/step-defects`),
  playlist: (p: string, planId: string) =>
    request<PlaylistEntry[]>(`${base(p)}/plans/${planId}/playlist`),
  submitRun: (p: string, planId: string, body: RunSubmitBody) =>
    request<RunResult>(`${base(p)}/plans/${planId}/runs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  chain: (p: string, planId: string, e2eId: string) =>
    request<ChainView>(`${base(p)}/plans/${planId}/e2e/${e2eId}/chain`),
  // #128: dieselben Kettensichten im Bündel, nach Ketten-human_id sortiert. Die Ids kommen aus der
  // eigenen Playlist — sie ist die einzige Quelle für „meine Ketten". Der Server nimmt je Abruf
  // höchstens CHAIN_BATCH_MAX Ids; mehr werden hier in Pakete geteilt und parallel geholt. Der
  // Server sortiert nur je Abruf, die Gesamtreihenfolge entsteht daher erst hier.
  chains: async (p: string, planId: string, e2eIds: string[]): Promise<ChainView[]> => {
    if (e2eIds.length === 0) return [];
    const batches: string[][] = [];
    for (let i = 0; i < e2eIds.length; i += CHAIN_BATCH_MAX) {
      batches.push(e2eIds.slice(i, i + CHAIN_BATCH_MAX));
    }
    const parts = await Promise.all(
      batches.map((ids) =>
        request<ChainView[]>(
          `${base(p)}/plans/${planId}/chains?e2e_ids=${ids.map(encodeURIComponent).join(',')}`,
        ),
      ),
    );
    return parts
      .flat()
      .sort((x, y) => x.human_id.localeCompare(y.human_id, undefined, { numeric: true }));
  },
  attachments: (p: string, planId: string, runId: string) =>
    request<RunAttachments>(`${base(p)}/plans/${planId}/runs/${runId}/attachments`),
  createRelease: (p: string, planId: string, e2eId: string, body: E2eReleaseCreate) =>
    request<E2eReleaseCreated>(`${base(p)}/plans/${planId}/e2e/${e2eId}/releases`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Task 8 (A5): Die zweite Freigabe fuer dasselbe Tripel ist KEIN Fehler, sondern eine Auskunft —
// jemand anders war schneller, und der Server schickt dessen Freigabe im 409-Rumpf mit (siehe
// runs.ts, `e2e_release_exists`). Muster wie `parseActivePlanConflict` in plans-api.ts: der
// Rumpf steckt als Text in `ApiError.message`. Liefert die BESTEHENDE Freigabe, wenn der Fehler
// genau dieser Konflikt ist — sonst null, damit der Aufrufer ihn wie jeden anderen Fehler
// behandelt. `release: null` im Rumpf (theoretisch moeglich, wenn die Zeile zwischen Konflikt und
// Nachlese verschwindet) faellt bewusst in denselben Zweig wie „kein Konflikt": ohne Freigabe gibt
// es nichts anzuzeigen.
export function parseReleaseConflict(err: unknown): ChainRelease | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.message) as {
      error?: string;
      release?: ChainRelease | null;
    };
    if (body.error === 'e2e_release_exists' && body.release) return body.release;
  } catch {
    // kein JSON-Rumpf -> ein anderer 409
  }
  return null;
}

// #31 (Findings 24/26): Lesesicht des Testers auf die eigenen Meldungen. Eigener Pfad, weil die
// Defect-Routen `defect.edit` verlangen und ein Tester das nicht hat.
export interface MyDefect {
  defect_id: string;
  human_id: string;
  title: string;
  status: string;
  severity_id: string | null;
  // #192: Testfall-Bezug und Anlagezeitpunkt des Defects (Worklist-Zeile, Sortierung).
  test_case_id: string | null;
  created_at: string;
  test_case_human_id: string | null;
  // #188: Titel des Testfalls (Anzeige per pickLang); die API liefert beide immer.
  test_case_title_de?: string | null;
  test_case_title_en?: string | null;
  my_description: string | null;
  // #192: null bei manuell angelegten Defects ohne Lauf (Liste „von mir gemeldet“).
  reported_at: string | null;
  run_human_id: string | null;
}

// #192: Einzel-Defect für die Nur-Lesen-Seitenleiste des Testers (GET /my-defects/:id). Eigener
// Typ statt DefectDetail aus defects-api: dessen Felder (Zuständiger, Plan, Fehlertyp …) liefert
// dieser Lesepfad bewusst nicht.
export interface MyDefectDetail {
  defect_id: string;
  human_id: string;
  title: string;
  // Die (ggf. vom Testmanager überarbeitete) Beschreibung; `my_description` ist der Wortlaut des Testers.
  description: string | null;
  status: DefectStatus;
  severity_id: string | null;
  test_case_id: string | null;
  test_case_human_id: string | null;
  test_case_title_de: string | null;
  test_case_title_en: string | null;
  failed_step: DefectFailedStep | null;
  screenshots: string[];
  my_description: string | null;
  reported_at: string | null;
  run_human_id: string | null;
  // null ohne Lauf (manueller Defect), sonst: Testfall stoppt die Kette (true) oder nicht (false).
  stops_chain: boolean | null;
  // false, wenn der Defect nur über den Retest-Chip sichtbar ist (lesen ja, mitschreiben nein).
  // #194: Prüfer (auch die Vertretung) dürfen bei offener Prüfung mitschreiben.
  can_add_note: boolean;
  // #194: true nur für den Melder selbst; "Meine Meldung" (my_description usw.) gibt es nur dann.
  is_own: boolean;
}

export interface MyDefectComment {
  comment_id: string;
  // #204: 'title_ai' = KI-Titelvorschlag (Systemzeile, body = vorheriger Auto-Titel, kein Autor).
  kind: 'comment' | 'status_change' | 'title_ai';
  body: string | null;
  from_status: string | null;
  to_status: string | null;
  created_at: string;
}

export const myDefectsApi = {
  list: (p: string) => request<MyDefect[]>(`/v1/projects/${p}/my-defects`),
  // #192: ein Defect mit Leserecht (eigener oder Retest-Chip); fremde und unbekannte sind 404.
  get: (p: string, id: string) => request<MyDefectDetail>(`/v1/projects/${p}/my-defects/${id}`),
  // #60/#61: „von mir gemeldet" — Defects, die dieser Nutzer angelegt hat (created_by), unabhängig
  // vom Lauf-Eigentum. Gleiche Ergebnis-Form wie `list`, damit die Dashboard-Worklist beide Reiter
  // (zugewiesen/gemeldet) gleich rendern kann. Serverseitige Sicherheitsgrenze: nur eigene Meldungen
  // (apps/api/src/routes/my-defects.ts, eq(created_by, me)).
  myReported: (p: string) => request<MyDefect[]>(`/v1/projects/${p}/my-defects/my-reported`),
  comments: (p: string, id: string) =>
    request<MyDefectComment[]>(`/v1/projects/${p}/my-defects/${id}/comments`),
  addNote: (p: string, id: string, body: string) =>
    request<MyDefectComment>(`/v1/projects/${p}/my-defects/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  // #194: offene Fehlerprüfungen des Nutzers (als Melder oder aktive Vertretung) über ALLE aktiven
  // Pläne — „Meine Tests" filtert selbst auf den gewählten Plan, damit der Deeplink `?fixcheck=` auch
  // eine Prüfung aus einem anderen Plan findet.
  fixChecks: (p: string) => request<FixCheck[]>(`/v1/projects/${p}/my-defects/fix-checks`),
  // #194: „Behoben" schließt, „Nicht behoben" (mit Pflichtbegründung) schickt in die Behebung zurück.
  // 409 `fix_check_not_open`, wenn der Defect inzwischen anders bearbeitet wurde.
  confirmFix: (p: string, id: string, body: FixCheckConfirm) =>
    request<{ status: DefectStatus }>(`/v1/projects/${p}/my-defects/${id}/confirm-fix`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
